import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';

// GraphQL mutation for subscribing to free tips
const SUBSCRIBE_TO_FREE_TIPS = gql`
  mutation SubscribeToFreeTips($email: String!) {
    subscribeToFreeTips(email: $email)
  }
`;

// Email validation regex
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const EmailCaptureForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Apollo mutation hook
  const [subscribeToFreeTips, { loading: isSubmitting }] = useMutation(SUBSCRIBE_TO_FREE_TIPS, {
    onCompleted: (data) => {
      if (data.subscribeToFreeTips) {
        setSuccess(true);
        setEmail('');
        setError('');
      }
    },
    onError: (error) => {
      console.error('Error subscribing to free tips:', error);
      setError(error.message || 'Failed to subscribe. Please try again.');
    }
  });

  // Validate email
  const validateEmail = (email: string): boolean => {
    if (!email) {
      setValidationError('Email is required');
      return false;
    }
    
    if (!emailRegex.test(email)) {
      setValidationError('Please enter a valid email address');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!validateEmail(email)) {
      return;
    }
    
    setError('');
    
    try {
      await subscribeToFreeTips({
        variables: { email }
      });
    } catch (error) {
      // Error is handled by onError callback
      console.error('Subscription error:', error);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(0,26rem)] gap-6 md:gap-10 items-center">
      <div className="flex items-start gap-4">
        <span className="flex-shrink-0 w-11 h-11 rounded-full bg-race-gold/15 border border-race-gold/40 flex items-center justify-center" aria-hidden="true">
          <svg className="w-5 h-5 text-race-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </span>
        <div>
          <h3 className="text-xl font-heading font-bold text-white mb-1">Get free weekly racing tips</h3>
          <p className="text-gray-300 text-sm">Our best tips and insights for the week ahead, straight to your inbox.</p>
        </div>
      </div>

      {success ? (
        <div className="flex items-center gap-2 bg-racing-green/15 border border-racing-green/40 text-racing-green-light p-4 rounded-md" role="status">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Thanks for subscribing!</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="email" className="sr-only">
            Email address
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              id="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`flex-grow min-w-0 h-12 px-4 rounded-md bg-white text-deep-navy focus:outline-none focus:ring-2 focus:ring-race-gold ${
                validationError ? 'ring-2 ring-bookie-red-light' : ''
              }`}
              placeholder="Your email address"
              disabled={isSubmitting}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`h-12 px-6 rounded-md bg-race-gold text-deep-navy font-bold hover:bg-race-gold-dark transition-colors flex items-center justify-center whitespace-nowrap ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Subscribing…' : 'Get free tips'}
            </button>
          </div>
          {validationError && <p className="mt-2 text-sm text-bookie-red-light">{validationError}</p>}
          {error && <p className="mt-2 text-sm text-bookie-red-light">{error}</p>}
          <p className="text-xs text-gray-400 mt-2">
            Racing tips and occasional offers. Unsubscribe any time from the link in any email.
          </p>
        </form>
      )}
    </div>
  );
};

export default EmailCaptureForm;
